import type { FormAsyncValidateOrFn, FormOptions, FormValidateOrFn, StandardSchemaV1 } from '@tanstack/form-core'
import { formOptions } from '@tanstack/react-form'
import type { z } from 'zod'

export function joinNames<TParent extends string, TChild extends string>(
  parent: TParent | { name: TParent },
  child: TChild,
): `${TParent}.${TChild}` {
  const parentName = typeof parent === 'string' ? parent : parent.name
  return `${parentName}.${child}`
}

/**
 * Builds `formOptions` strictly typed against a Zod schema.
 *
 * `TFormData` is locked to `z.output<T>`, so `defaultValues`, field paths,
 * and `value` on every callback flow from the schema. `onMount` and
 * `onChange` are wired to the schema by default; pass `validators` in
 * `defaultOpts` to add or override (e.g. async validators).
 *
 * @example
 *   useAppForm(formSchemaOptions(Schema, {
 *     defaultValues: row,
 *     onSubmit: async ({ value }) => save(value),
 *   }))
 */
export function formSchemaOptions<
  T extends z.ZodType,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnBlur extends undefined | FormValidateOrFn<z.output<T>> = undefined,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnSubmit extends undefined | FormValidateOrFn<z.output<T>> = undefined,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnDynamic extends undefined | FormValidateOrFn<z.output<T>> = undefined,
  TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnServer extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TSubmitMeta = never,
>(
  schema: T,
  defaultOpts: Partial<
    FormOptions<
      z.output<T>,
      StandardSchemaV1<z.output<T>>,
      StandardSchemaV1<z.output<T>>,
      TOnChangeAsync,
      TOnBlur,
      TOnBlurAsync,
      TOnSubmit,
      TOnSubmitAsync,
      TOnDynamic,
      TOnDynamicAsync,
      TOnServer,
      TSubmitMeta
    >
  > = {},
): FormOptions<
  z.output<T>,
  StandardSchemaV1<z.output<T>>,
  StandardSchemaV1<z.output<T>>,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnDynamic,
  TOnDynamicAsync,
  TOnServer,
  TSubmitMeta
> {
  return formOptions({
    ...defaultOpts,
    validators: {
      onMount: schema,
      onChange: schema,
      ...defaultOpts.validators,
    },
  } as never)
}
